import { type StreamingStatus } from '@/models/scribeRecordModel';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useQuerySessionById } from '@heidi/session/data';
import { useQueryDocumentById } from './useQueryDocumentById';
import { TabType, useSessionWindow, useSessionWindows } from './useQuerySessionWindows';

export const useQuerySessionStreamingStatus = (
  sessionId: string
): StreamingStatus | undefined => {
  const { data: streamingStatus } = useQuerySessionById({
    sessionId,
    select: (session) => session?.streamingStatus,
  });
  return streamingStatus;
};

export const useQueryDocumentStreamingStatus = ({
  sessionId,
  documentId,
}: {
  sessionId: string;
  documentId: string;
}): StreamingStatus | undefined => {
  const { data: streamingStatus } = useQueryDocumentById({
    sessionId,
    documentId,
    select: (document) => document?.streamingStatus,
  });
  return streamingStatus;
};

export const useQueryCurrentTabStreamingStatus = () => {
  const sessionId = useCurrentSessionId();
  const { activeWindowIndex } = useSessionWindows({ sessionId });
  const tabNameFromUseQuery = useSessionWindow({
    sessionId,
    windowIndex: activeWindowIndex,
  })?.activeTab?.name;
  const documentId = useSessionWindow({
    sessionId,
    windowIndex: activeWindowIndex,
  })?.activeTab?.documentId;
  const noteStreamingStatus = useQuerySessionStreamingStatus(sessionId);
  const documentStreamingStatus = useQueryDocumentStreamingStatus({
    sessionId,
    documentId: documentId ?? '',
  });
  if (tabNameFromUseQuery === TabType.NOTE) {
    return noteStreamingStatus;
  } else if (tabNameFromUseQuery === TabType.DOCUMENT && documentId) {
    return documentStreamingStatus;
  }
  return undefined;
};

export const useActiveDocumentId = () => {
  const sessionId = useCurrentSessionId();
  const { activeWindowIndex } = useSessionWindows({ sessionId });
  const activeWindow = useSessionWindow({
    sessionId,
    windowIndex: activeWindowIndex,
  });

  if (!activeWindow?.activeTab) {
    return undefined;
  }
  return activeWindow.activeTab?.documentId;
};
